import React from "react";
import {
  Box,
  Typography,
  CardContent,
  LinearProgress,
  Divider,
} from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { formatCurrency } from "../../../utils/currencyUtils";
import { StudentFeeLedgerStatsDto } from "../../../shared/dtos/studentFeeLedger/StudentFeeLedgerStatsDto";

interface StudentFeeLedgerWidgetProps {
  data: StudentFeeLedgerStatsDto;
}

const StudentFeeLedgerWidget: React.FC<StudentFeeLedgerWidgetProps> = ({
  data,
}) => {
  const meta = getEntityMeta("STUDENT_FEE_LEDGER");
  const Icon = meta.icon;

  const isLow = data.collectionPercentage < 50;

  return (
    <StyledCard sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {meta.title}
          </Typography>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            Overall Collection Rate
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: isLow ? "error.main" : "success.main",
              }}
            >
              {data.collectionPercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={data.collectionPercentage}
            color={isLow ? "error" : "success"}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Billed
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {formatCurrency(data.totalBilled)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Paid
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "success.main" }}
            >
              {formatCurrency(data.totalPaid)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Arrears
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "error.main" }}
            >
              {formatCurrency(data.outstandingArrears)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default StudentFeeLedgerWidget;
